import {Injectable} from '@angular/core';
import {ISession} from '../models/ISession.interface';
import {BackendService} from './backend.service';
import { EventQueueService } from './event-queue-service.service';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Rolle } from '../models/Rolle.enum';
import { toGetOptions, assertionToJson } from '../utils/webauthn';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private _session: ISession = {sessionId: null, username: null, nutzer: null, currentPermission: Rolle.WAIT};
  private navigateAfterRestore: Parameters<Router["navigate"]>;
  private changePasswordToastId: number = 0;
  /** Gesetzt, wenn das Passwort korrekt war und der zweite Faktor aussteht */
  public mfaChallenge: {mfaToken: string, methode: 'MAIL' | 'TOTP', username: string} | null = null;
  /** Gesetzt, wenn das Passwort korrekt war, die MFA-Pflicht aktiv ist und noch kein zweiter Faktor eingerichtet ist */
  public mfaSetupChallenge: {setupToken: string, remainingPostpones: number, username: string} | null = null;

  constructor(
    private backend: BackendService,
    private eventQueue: EventQueueService,
    private router: Router,
    private toastr: ToastrService) {
    this.eventQueue.getObservable().subscribe(msg => this.checkPasswordUpdateRequired(msg));
  }

  public async restoreSession(): Promise<void> {
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      const result = await this.backend.sendRequest('checkSession', {
        sessionId: sessionId
      }, false);

      if (result.success) {
        this._session.sessionId = sessionId;
        this._session.username = result.username;
        this._session.currentPermission = result.currentPermission;

        BackendService.updateIdToProperty(this._session, 'nutzer', () => (window as any).nutzerService.getNutzerByUsername(result.username), false, () => (window as any).nutzerService.getRevision());
        this.eventQueue.fireEvent('permissionChange');
        this.toastr.success('Sitzung wiederhergestellt', 'Erfolgreiche Anmeldung');
        await this.router.navigate(...this.navigateAfterRestore);
      }
    }
  }

  public checkPasswordUpdateRequired(msg: String) {
    if(msg != 'initDone') {
      return;
    }
    if(!this._session.nutzer){
      return;
    }
    if(!this._session.nutzer.last_password_change && !this.changePasswordToastId) {
      this.changePasswordToastId = this.toastr.info(
        'Ihr Passwort entspricht nicht mehr den aktuellen Anforderungen und muss in den Einstellungen geändert werden!',
        'Sicherheitswarnung',
        {
          closeButton: false,
          disableTimeOut: true,
          tapToDismiss: false,
          positionClass: "toast-bottom-left",
          enableHtml: true
        }).toastId;
    } else if(this._session.nutzer.last_password_change && this.changePasswordToastId) {
      this.toastr.clear(this.changePasswordToastId);
    }
  }

  public login(username: string, password: string): void {
    const result = this.backend.sendRequest('login', {
      username: username,
      password: password
    }, false);

    result.then(session => {
      if ((session as any).mfaRequired) {
        // Passwort korrekt, zweiter Faktor steht aus
        this.mfaChallenge = {
          mfaToken: (session as any).mfaToken,
          methode: (session as any).methode,
          username: username
        };
        if (this.mfaChallenge.methode === 'MAIL') {
          this.toastr.info('Ein Anmeldecode wurde an Ihre E-Mail-Adresse gesendet.', 'Code erforderlich');
        } else {
          this.toastr.info('Bitte den Code aus Ihrer Authenticator-App eingeben.', 'Code erforderlich');
        }
        return;
      }
      if (session.mfaSetupRequired) {
        // Passwort korrekt, aber zweiter Faktor ist Pflicht und muss erst eingerichtet werden
        this.mfaSetupChallenge = {
          setupToken: session.setupToken,
          remainingPostpones: session.remainingPostpones,
          username: username
        };
        return;
      }
      if (session.sessionId === null) {
        console.log(session);
        this._session = {sessionId: null, username: null, nutzer: null, currentPermission: Rolle.WAIT};
        this.toastr.error(session.msg, 'Fehlerhafte Anmeldung');
        return;
      }

      this.applySession(session, username);
    }).catch(error => {
      console.log(error);
      this._session = {sessionId: null, username: null, nutzer: null, currentPermission: Rolle.WAIT};
      this.toastr.error('Anmeldung fehlgeschlagen', 'Fehlerhafte Anmeldung');
    });
  }

  /** Zweiter Login-Schritt: Mail-Code bzw. TOTP bestätigen */
  public async verifyMfa(code: string): Promise<void> {
    if (!this.mfaChallenge) {
      return;
    }
    const username = this.mfaChallenge.username;
    try {
      const session = await this.backend.sendRequest('verifyMfa', {
        mfaToken: this.mfaChallenge.mfaToken,
        code: code
      }, false);

      if (session.sessionId === null) {
        if (!(session as any).mfaRequired) {
          // Token abgelaufen oder verbrannt: zurück zur Passworteingabe
          this.mfaChallenge = null;
        }
        this.toastr.error((session as any).msg || 'Der Code ist ungültig.', 'Fehlerhafte Anmeldung');
        return;
      }

      this.mfaChallenge = null;
      this.applySession(session, username);
    } catch (error) {
      console.log(error);
      this.toastr.error('Anmeldung fehlgeschlagen', 'Fehlerhafte Anmeldung');
    }
  }

  public cancelMfa(): void {
    this.mfaChallenge = null;
  }

  /** MFA-Pflicht: TOTP-Einrichtung beim Login starten (noch ohne Session) */
  public async startTotpSetupForLogin(): Promise<{secret: string, otpauthUrl: string} | null> {
    if (!this.mfaSetupChallenge) {
      return null;
    }
    const result = await this.backend.sendRequest('startTotpSetupForLogin', {
      setupToken: this.mfaSetupChallenge.setupToken
    }, false);
    if (!result.success) {
      this.mfaSetupChallenge = null;
      this.toastr.error(result.error || 'Fehler beim Starten der Einrichtung', 'Fehlerhafte Anmeldung');
      return null;
    }
    return {secret: result.secret, otpauthUrl: result.otpauthUrl};
  }

  /** MFA-Pflicht: zweiten Faktor einrichten und damit anmelden */
  public async completeMfaSetup(methode: 'MAIL' | 'TOTP', code: string): Promise<void> {
    if (!this.mfaSetupChallenge) {
      return;
    }
    await this.finishMfaSetupRequest(this.backend.sendRequest('completeMfaSetup', {
      setupToken: this.mfaSetupChallenge.setupToken,
      methode: methode,
      code: code
    }, false), 'Zweiter Faktor wurde eingerichtet');
  }

  /** MFA-Pflicht: Einrichtung aufschieben und ohne zweiten Faktor anmelden */
  public async postponeMfaSetup(): Promise<void> {
    if (!this.mfaSetupChallenge) {
      return;
    }
    await this.finishMfaSetupRequest(this.backend.sendRequest('postponeMfaSetup', {
      setupToken: this.mfaSetupChallenge.setupToken
    }, false), null);
  }

  private async finishMfaSetupRequest(request: Promise<any>, successMsg: string | null): Promise<void> {
    const username = this.mfaSetupChallenge.username;
    try {
      const session = await request;
      if (session.sessionId === null) {
        if (session.mfaSetupRequired) {
          this.mfaSetupChallenge.remainingPostpones = session.remainingPostpones;
        } else {
          // Token abgelaufen oder verbrannt: zurück zur Passworteingabe
          this.mfaSetupChallenge = null;
        }
        this.toastr.error(session.msg || 'Anmeldung fehlgeschlagen', 'Fehlerhafte Anmeldung');
        return;
      }
      this.mfaSetupChallenge = null;
      if (successMsg) {
        this.toastr.success(successMsg, '2. Faktor');
      }
      this.applySession(session, username);
    } catch (error) {
      console.log(error);
      this.toastr.error('Anmeldung fehlgeschlagen', 'Fehlerhafte Anmeldung');
    }
  }

  public cancelMfaSetup(): void {
    this.mfaSetupChallenge = null;
  }

  /** Login per Passkey (WebAuthn): ersetzt Passwort und zweiten Faktor in einem Schritt */
  public async loginWithPasskey(): Promise<void> {
    try {
      const start = await this.backend.sendRequest('startPasskeyLogin', {}, false);
      if (!start.loginToken) {
        this.toastr.error(start.msg || 'Der Passkey-Login konnte nicht gestartet werden.', 'Fehlerhafte Anmeldung');
        return;
      }

      const credential = await navigator.credentials.get(toGetOptions(start.options));

      const session = await this.backend.sendRequest('finishPasskeyLogin', {
        loginToken: start.loginToken,
        credential: assertionToJson(credential)
      }, false);

      if (session.sessionId === null) {
        this.toastr.error(session.msg || 'Anmeldung fehlgeschlagen', 'Fehlerhafte Anmeldung');
        return;
      }

      this.applySession(session, (session as any).username);
    } catch (error) {
      if (error && error.name === 'NotAllowedError') {
        // Nutzer hat den Browser-Dialog abgebrochen
        return;
      }
      console.log(error);
      this.toastr.error('Anmeldung fehlgeschlagen', 'Fehlerhafte Anmeldung');
    }
  }

  private applySession(session: any, username: string): void {
    BackendService.updateIdToProperty(session, 'nutzer', (id) => (window as any).nutzerService.getNutzer(id), false, () => (window as any).nutzerService.getRevision());
    Object.defineProperty(session, 'username', {get: () => username, enumerable: true});
    this._session = session as unknown as ISession;
    localStorage.setItem('sessionId', this._session.sessionId);
    this.eventQueue.fireEvent('permissionChange');
    this.router.navigate(['/event']);
    this.toastr.success('Sie haben sich erfolgreich angemeldet', 'Erfolgreiche Anmeldung');
  }

  public get session() {
    return this._session;
  }

  public logout() {
    this.backend.sendRequest('logout', {sessionId: this._session.sessionId}, false);
    this._session = {sessionId: null, username: null, nutzer: null, currentPermission: Rolle.WAIT};
    localStorage.removeItem('sessionId');
    this.router.navigate(['/home']);
    this.eventQueue.fireEvent('permissionChange');
    this.toastr.success('Sie haben sich erfolgreich abgemeldet', 'Erfolgreiche Abmeldung');
  }

  public togglePermission() {
    if(this.session.currentPermission === this.session.nutzer.rolle) {
      this.backend.sendRequest('setSessionPermissions', {
        sessionId: this.session.sessionId,
        newPermissions: Rolle.SL
      }, true).then(_ => {
        this.session.currentPermission = Rolle.SL;
      });
    } else {
      this.backend.sendRequest('setSessionPermissions', {
        sessionId: this.session.sessionId,
        newPermissions: this.session.nutzer.rolle
      }, true).then(_ => {
        this.session.currentPermission = this.session.nutzer.rolle;
      });
    }
  }

  // Check Funktion fürs Routing
  canActivate(route: ActivatedRouteSnapshot, _: RouterStateSnapshot): boolean {
    const ignorError = !this.navigateAfterRestore;
    this.navigateAfterRestore = [[route.url.join('/')], {
      queryParams: route.queryParams,
      queryParamsHandling: "merge"
    }];
    const isLoggedIn = this._session && this._session.username;
    if (isLoggedIn) {
      return true;
    } else if (ignorError) {
      this.router.navigate(['/home'], {queryParams:route.queryParams, queryParamsHandling: "merge"});
      return false;
    } else {
      this.router.navigate(['/home'], {queryParams:route.queryParams, queryParamsHandling: "preserve"});
      this.toastr.warning('Navigation nicht möglich. Bitte einloggen!', 'Keine Berechtigung');
      return false;
    }

  }
}
